import { apiClient } from '@/lib/api-client';
import type { Consent, ConsentMethod, ConsentTexts } from '@/types/api';

export type ConsentInput = ConsentTexts & {
  patientId: string;
  methodId: string | null;
  methodName: string;
  site: string | null;
  representativeName: string | null;
  note: string | null;
  dentistId?: string | null;
};

export type ConsentMethodInput = ConsentTexts & { name: string };

export const consentsApi = {
  /** Newest first; `q` matches the number, the patient or the method. */
  async list(params: { q?: string; patientId?: string; limit?: number }) {
    const { data } = await apiClient.get<Consent[]>('/consents', {
      params: { q: params.q || undefined, patientId: params.patientId, limit: params.limit ?? 50 },
    });
    return data;
  },
  async get(id: string) {
    const { data } = await apiClient.get<Consent>(`/consents/${id}`);
    return data;
  },
  async create(input: ConsentInput) {
    const { data } = await apiClient.post<Consent>('/consents', {
      ...input,
      dentistId: input.dentistId ?? undefined,
    });
    return data;
  },
  /** The clinic's invasive methods in list order; `all` includes retired ones. */
  async methods(all = false) {
    const { data } = await apiClient.get<ConsentMethod[]>('/consent-methods', {
      params: all ? { all: 'true' } : undefined,
    });
    return data;
  },
  async createMethod(input: ConsentMethodInput) {
    const { data } = await apiClient.post<ConsentMethod>('/consent-methods', input);
    return data;
  },
  async updateMethod(
    id: string,
    patch: Partial<ConsentMethodInput & { active: boolean; sortOrder: number }>
  ) {
    const { data } = await apiClient.patch<ConsentMethod>(`/consent-methods/${id}`, patch);
    return data;
  },
};
